import React, { memo } from 'react';
import { useTheme } from '../../../../context/ThemeProvider';
import {
  QuestionAttemptStatus,
  QuestionAttemptStatusType,
} from '../../../../types/assignmentTypes';
import { getQuestionAttemptStatus } from '../../utils/AssignmentHelpers';
import { useAssignmentContext } from '../../utils/AssignmentProvider';
import './styles.scss';

const AssignmentReviewSubmit: React.FC<{
  onClickQuestion: (index: number) => void;
}> = ({ onClickQuestion }) => {
  const { colors } = useTheme();
  const { flow, questionsMapByIndex } = useAssignmentContext();

  const STYLES: Record<
    QuestionAttemptStatusType,
    {
      buttonBorderColor: string;
      buttonBgColor: string;
      iconBorderColor: string;
      iconBgColor: string;
      iconTextColor?: string;
    }
  > = {
    [QuestionAttemptStatus.SKIPPED]: {
      buttonBorderColor: colors.BORDER,
      buttonBgColor: colors.BACKGROUND,
      iconBorderColor: colors.BORDER,
      iconBgColor: colors.BACKGROUND,
      iconTextColor: colors.TEXT1,
    },
    [QuestionAttemptStatus.ATTEMPTED]: {
      buttonBorderColor: colors.BORDER,
      buttonBgColor: colors.PRIMARY_LIGHT,
      iconBorderColor: colors.BORDER,
      iconBgColor: colors.PRIMARY,
      iconTextColor: colors.BACKGROUND,
    },
    [QuestionAttemptStatus.CORRECT]: {
      buttonBorderColor: colors.GREEN,
      buttonBgColor: colors.GREEN,
      iconBorderColor: colors.GREEN,
      iconBgColor: colors.GREEN,
      iconTextColor: colors.TEXT2,
    },
    [QuestionAttemptStatus.WRONG]: {
      buttonBorderColor: colors.DANGER,
      buttonBgColor: colors.DANGER,
      iconBorderColor: colors.DANGER,
      iconBgColor: colors.DANGER,
      iconTextColor: colors.TEXT2,
    },
  };

  const toTitleCase = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  return (
    <div className="assignment-review-submit">
      <div className="assignment-review-submit__header">
        <p className="assignment-review-submit__description">
          Click on a question to review your answer or attempt skipped ones
          before final submission.
        </p>
      </div>

      <div className="assignment-review-submit__legend">
        {['attempted', 'skipped'].map((status) => (
          <div key={status} className="assignment-review-submit__legend-item">
            <div
              className="assignment-review-submit__legend-indicator"
              style={{
                backgroundColor:
                  status === QuestionAttemptStatus.ATTEMPTED
                    ? colors.PRIMARY
                    : colors.BORDER,
                border:
                  status === QuestionAttemptStatus.ATTEMPTED
                    ? 'none'
                    : `2px solid ${colors.BORDER}`,
              }}
            />
            <span className="assignment-review-submit__legend-text">
              {toTitleCase(status)}
            </span>
          </div>
        ))}
      </div>

      <div className="assignment-review-submit__questions">
        {Object.keys(questionsMapByIndex).map((index: string) => {
          const questionIndex = Number(index);
          const question = questionsMapByIndex[questionIndex];
          const status = getQuestionAttemptStatus(question, flow);

          return (
            <button
              onClick={() => onClickQuestion(questionIndex)}
              key={question._id}
              className="assignment-review-submit__question-button"
              style={{
                backgroundColor: STYLES[status].buttonBgColor,
                borderColor: STYLES[status].buttonBorderColor,
              }}>
              <div
                className="assignment-review-submit__question-icon"
                style={{
                  backgroundColor: STYLES[status].iconBgColor,
                  borderColor: STYLES[status].iconBorderColor,
                }}>
                <span
                  className="assignment-review-submit__question-number"
                  style={{ color: STYLES[status].iconTextColor }}>
                  {question.order}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default memo(AssignmentReviewSubmit);
